import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Select, SelectOptionType } from '../../atoms/Select';
import { MultiSelect } from '../../atoms/MultiSelect';
import { Input } from '../../atoms/Input';
import { Button } from '../../atoms/Button';
import { COLOR_LIGHT_BLUE, COLOR_PRIMARY, COLOR_WHITE } from '../../../constants/color';
import { useSearchFormData } from './useSearchFormData';
import { useLanguage } from '../../../contexts/LanguageContext';
import { PriceRangeSelector } from '../../molecules/PriceRangeSelector';



// Hardcoded options that don't need API calls move inside component for translation

const bedroomsOptions: SelectOptionType[] = [
  { id: 1, name: '1', value: 1 },
  { id: 2, name: '2', value: 2 },
  { id: 3, name: '3', value: 3 },
  { id: 4, name: '4', value: 4 },
  { id: 5, name: '5', value: 5 },
  { id: 6, name: '6', value: 6 },
  { id: 7, name: '7+', value: 7 },
];



export type SearchFormType = 'residential' | 'commercial';

export interface SearchFormProps {
  type?: SearchFormType;
  onSubmit?: (values: any) => void;
  showTabs?: boolean;
  categoryType?: string; // For filtering property types by category
  initialValues?: {
    propertyStatus?: string;
    propertyType?: string;
    district?: string;
    commune?: string;
    popularPlace?: string;
    bedrooms?: string;
    minPrice?: string;
    maxPrice?: string;
    propertyCode?: string;
  };
}

export const SearchForm: React.FC<SearchFormProps> = ({
  type = 'residential',
  onSubmit,
  showTabs = true,
  categoryType,
  initialValues
}) => {
  const { t } = useLanguage();
  const [searchType, setSearchType] = useState<SearchFormType>(type);
  const [initialLoad, setInitialLoad] = useState(true);

  const SEARCH_TYPES = [
    { key: 'residential', label: t('property.residential') },
    { key: 'commercial', label: t('property.commercial') },
  ];
  const [form, setForm] = useState({
    propertyStatus: initialValues?.propertyStatus || '',
    propertyType: initialValues?.propertyType || '',
    district: initialValues?.district || '',
    commune: initialValues?.commune || '',
    popularPlace: initialValues?.popularPlace || '',
    bedrooms: initialValues?.bedrooms || '',
    minPrice: initialValues?.minPrice || '',
    maxPrice: initialValues?.maxPrice || '',
    propertyCode: initialValues?.propertyCode || '',
  });

  const propertyStatusOptions: SelectOptionType[] = [
    { id: 1, name: t('property.status.sale'), value: 'sale' },
    { id: 2, name: t('property.status.rent'), value: 'rent' },
  ];

  // Remove priceRangeOptions as we use custom inputs now

  // Determine categoryType based on searchType (residential=1, commercial=2)
  const currentCategoryType = categoryType || (searchType === 'residential' ? '1' : '2');

  // Use the custom hook to fetch dropdown data
  const {
    propertyTypes,
    popularPlaces,
    districts,
    communes,
    loading: dataLoading,
    fetchCommunes
  } = useSearchFormData({ categoryType: currentCategoryType });

  // Track when initial loading is complete
  useEffect(() => {
    if (!dataLoading && initialLoad) {
      setInitialLoad(false);
    }
  }, [dataLoading, initialLoad]);

  // Update form when initialValues change (e.g., from navigation)
  useEffect(() => {
    if (initialValues) {
      setForm({
        propertyStatus: initialValues.propertyStatus || '',
        propertyType: initialValues.propertyType || '',
        district: initialValues.district || '',
        commune: initialValues.commune || '',
        popularPlace: initialValues.popularPlace || '',
        bedrooms: initialValues.bedrooms || '',
        minPrice: initialValues.minPrice || '',
        maxPrice: initialValues.maxPrice || '',
        propertyCode: initialValues.propertyCode || '',
      });

      // If district is provided, fetch communes for that district
      if (initialValues.district) {
        fetchCommunes(initialValues.district.toString());
      }
    }
  }, [initialValues, fetchCommunes]);

  // Create a unique key to force re-render when initial values change
  const formKey = initialValues ? JSON.stringify(initialValues) : 'empty';

  // Helper function to find the option name by value
  const findOptionNameByValue = (options: SelectOptionType[], value: string | undefined) => {
    if (!value) return '';
    const option = options.find(opt => opt.value.toString() === value.toString());
    return option ? option.name : '';
  };

  const handleChange = (field: string, value: any) => {
    // When district changes, handle both district and commune in single update
    if (field === 'district') {
      setForm((prev) => ({ ...prev, [field]: value, commune: '' }));

      if (value) {
        // District selected: fetch communes for that district
        fetchCommunes(value.toString());
      }
    } else {
      // For all other fields, just update normally
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleTabChange = (tab: SearchFormType) => {
    setSearchType(tab);
    // Clear all form fields when switching tabs since property types will change
    setForm({
      propertyStatus: '',
      propertyType: '',
      district: '',
      commune: '',
      popularPlace: '',
      bedrooms: '',
      minPrice: '',
      maxPrice: '',
      propertyCode: '',
    });
  };

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    if (onSubmit) onSubmit({ ...form, type: searchType });
  };

  // Show loading state only during initial load
  if (dataLoading && initialLoad) {
    return (
      <Wrapper>
        {/* Skeleton Header */}
        <HeaderBox>
          <SkeletonTitle $width={240} $height={22} />
        </HeaderBox>

        {/* Skeleton Form */}
        <FormBox>
          <SkeletonTitle $width={220} $height={18} />
          <Row>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
          </Row>
          <Row>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
            <FieldWrapper><SkeletonInput $height={49} /></FieldWrapper>
            <FieldWrapper><SkeletonInput $height={40} /></FieldWrapper>
          </Row>
          <ButtonRow>
            <SkeletonButton $width={160} $height={48} />
          </ButtonRow>
        </FormBox>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {showTabs ? (
        <TabContainer>
          {SEARCH_TYPES.map((tab) => (
            <TabButton
              key={tab.key}
              $active={searchType === tab.key}
              onClick={() => handleTabChange(tab.key as SearchFormType)}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabContainer>
      ) : (
        <HeaderBox>
          <HeaderTitle>{t(`property.${type}`)}</HeaderTitle>
        </HeaderBox>
      )}
      <FormBox>
        <Title>{t('property.findYourDreamProperty')}</Title>
        <Row>
          <FieldWrapper>
            <Select
              key={`propertyStatus-${searchType}-${formKey}`}
              name="propertyStatus"
              label=""
              placeholder={t('property.propertyStatus')}
              options={propertyStatusOptions}
              onChange={(_, value) => handleChange('propertyStatus', value)}
              defaultValue={findOptionNameByValue(propertyStatusOptions, form.propertyStatus)}
              width="100%"
              height="49px"
            />
          </FieldWrapper>
          <FieldWrapper>
            <Select
              key={`propertyType-${searchType}-${formKey}`}
              name="propertyType"
              label=""
              placeholder={t('property.propertyType')}
              options={propertyTypes}
              onChange={(_, value) => handleChange('propertyType', value)}
              defaultValue={findOptionNameByValue(propertyTypes, form.propertyType)}
              width="100%"
              height="49px"
            />
          </FieldWrapper>
          <FieldWrapper>
            <Select
              key={`district-${searchType}-${formKey}`}
              name="district"
              label=""
              placeholder={t('property.districts')}
              options={districts}
              onChange={(_, value) => handleChange('district', value)}
              defaultValue={findOptionNameByValue(districts, form.district)}
              width="100%"
              height="49px"
            />
          </FieldWrapper>
          <FieldWrapper>
            <Select
              key={`commune-${form.district || 'empty'}-${searchType}-${formKey}`}
              name="commune"
              label=""
              placeholder={t('property.communes')}
              options={communes}
              onChange={(_, value) => handleChange('commune', value)}
              defaultValue={findOptionNameByValue(communes, form.commune)}
              width="100%"
              height="49px"
              disabled={!form.district}
            />
          </FieldWrapper>
        </Row>
        <Row>
          <FieldWrapper>
            <Select
              key={`popularPlace-${searchType}-${formKey}`}
              name="popularPlace"
              label=""
              placeholder={t('property.popularPlaces')}
              options={popularPlaces}
              onChange={(_, value) => handleChange('popularPlace', value)}
              defaultValue={findOptionNameByValue(popularPlaces, form.popularPlace)}
              width="100%"
              height="49px"
            />
          </FieldWrapper>
          <FieldWrapper>
            <MultiSelect
              key={`bedrooms-${searchType}-${formKey}`}
              name="bedrooms"
              label=""
              placeholder={t('property.bedrooms')}
              options={bedroomsOptions}
              onChange={(_, value) => handleChange('bedrooms', value.join(','))}
              defaultValue={form.bedrooms ? form.bedrooms.toString().split(',') : []}
              width="100%"
              height="49px"
            />
          </FieldWrapper>
          <FieldWrapper>
            <PriceRangeSelector
              minPrice={form.minPrice}
              maxPrice={form.maxPrice}
              placeholder={t('property.priceRange')}
              onChange={(field, value) => handleChange(field, value)}
              width="100%"
              height="49px"
            />
          </FieldWrapper>
          <FieldWrapper>
            <Input
              key={`propertyCode-${searchType}-${formKey}`}
              name="propertyCode"
              placeholder={t('property.propertyCode')}
              value={form.propertyCode}
              onChange={(value) => handleChange('propertyCode', value)}
              height="40px"
              width="100%"
            />
          </FieldWrapper>
        </Row>
        <ButtonRow>
          <Button text={t('property.search')} onClick={handleSubmit} width="160px" height="48px" backgroundColor='#A9C7E8' textColor="#06163A" />
        </ButtonRow>
      </FormBox>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  width: 100%;
  max-width: 1200px;
  margin: 16px;
`;

// Skeleton styles
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div<{ $width?: number | string; $height?: number | string }>`
  width: ${({ $width }) => (typeof $width === 'number' ? `${$width}px` : $width || '100%')};
  height: ${({ $height }) => (typeof $height === 'number' ? `${$height}px` : $height || '16px')};
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.18) 37%, rgba(255,255,255,0.08) 63%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SkeletonTitle = styled(SkeletonBase)`
  margin: 4px 0 12px 0;
`;

const SkeletonInput = styled(SkeletonBase) <{ $height?: number }>`
  width: 100%;
`;

const SkeletonButton = styled(SkeletonBase) <{ $width?: number; $height?: number }>`
  width: ${({ $width }) => ($width ? `${$width}px` : '160px')};
  height: ${({ $height }) => ($height ? `${$height}px` : '48px')};
`;

const TabContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: -1px;
  width: 100%;
  max-width: 900px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? COLOR_PRIMARY : COLOR_WHITE)};
  color: ${({ $active }) => ($active ? COLOR_WHITE : COLOR_PRIMARY)};
  font-weight: 700;
  font-size: 20px;
  border: none;
  border-radius: 8px 8px 0 0;
  padding: 16px 40px;
  margin-right: 2px;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? '0 -2px 8px rgba(0,0,0,0.04)' : 'none')};
  border-bottom: ${({ $active }) => ($active ? 'none' : '2px solid #e9f0fa')};
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 20px;
  }
`;

const FormBox = styled.div`
  background: ${COLOR_PRIMARY};
  border-radius: 0 0 5px 5px;
  padding: 20px 32px 24px 32px;
  width: 100%;
  max-width: 900px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  display: flex;
  flex-direction: column;
  gap: 18px;
  
  @media (max-width: 1100px) {
    padding: 24px 20px;
    margin: 0 16px;
  }
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    margin: 0 8px;
  }
`;

const Title = styled.div`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    font-size: 16px;
    text-align: center;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const FieldWrapper = styled.div`
  min-width: 0; // Prevents flex items from overflowing
  width: 100%;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
  
  button {
    background: ${COLOR_LIGHT_BLUE}
  }
`;

const HeaderBox = styled.div`
  background: ${COLOR_PRIMARY};
  border-radius: 5px 5px 0 0;
  padding: 20px 32px;
  width: 100%;
  max-width: 900px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  border-bottom: 2px solid #fff;
  
  @media (max-width: 1100px) {
    padding: 24px 20px;
    margin: 0 16px;
  }
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    margin: 0 8px;
  }
`;

const HeaderTitle = styled.div`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`; 